import { useCallback, useEffect, useRef, useState } from "react";
import { $apiV1 } from "../../../../config/api";
import { findMaxSecondMarker } from "../../../../helpers";
import {
  buildMarkerColorMap,
  buildParameterColorMap,
} from "../../../../helpers/eventMarkerColors";
import {
  EVENTS_RESULT_LIMIT,
  fetchEventsResultCount,
} from "../eventsResultLimit";

const DEFAULT_TIMELINE_RANGE = { min: 0, max: 90 };

export const useTeamDualSideEventsData = ({
  id,
  matchesList,
  checkedIds,
  checkedAction,
  mergeOffset,
  buildSearchPayload,
  includeGoalsConceded = false,
  onError,
  searchDeps = [],
  keepMaxTimeline = false,
  initialTimelineRange = DEFAULT_TIMELINE_RANGE,
}) => {
  const initialTimelineMin =
    initialTimelineRange?.min ?? DEFAULT_TIMELINE_RANGE.min;
  const initialTimelineMax =
    initialTimelineRange?.max ?? DEFAULT_TIMELINE_RANGE.max;

  const [markers, setMarkers] = useState([]);
  const [opponentsMarkers, setOpponentsMarkers] = useState([]);
  const [response, setResponse] = useState(null);
  const [responseOpponents, setReponseOpponents] = useState(null);

  const [ownMarkerColorMap, setOwnMarkerColorMap] = useState({});
  const [opponentMarkerColorMap, setOpponentMarkerColorMap] = useState({});

  const [allOwnMarkers, setAllOwnMarkers] = useState([]);
  const [allOpponentsMarkers, setAllOpponentsMarkers] = useState([]);
  const [allMergeMarkers, setAllMergeMarkers] = useState([]);

  const [loadingMarkers, setLoadingMarkers] = useState(false);
  // Per-side result-count guard: null = within limit, otherwise the (over-limit) count to display.
  const [limitExceededBySide, setLimitExceededBySide] = useState({
    own: null,
    opponent: null,
  });
  const [timeLineRange, setTimeLineRange] = useState({
    min: initialTimelineMin,
    max: initialTimelineMax,
  });

  const goalsConcededMarkersRef = useRef([]);
  const goalsConcededOpponentsMarkersRef = useRef([]);
  const pendingRequestsRef = useRef(0);
  const timelineMaxRef = useRef(0);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const updateLoading = useCallback((delta) => {
    pendingRequestsRef.current += delta;
    setLoadingMarkers(pendingRequestsRef.current > 0);
  }, []);

  const resetData = useCallback(() => {
    setMarkers([]);
    setOpponentsMarkers([]);
    setResponse(null);
    setReponseOpponents(null);
    setOwnMarkerColorMap({});
    setOpponentMarkerColorMap({});
    setAllOwnMarkers([]);
    setAllOpponentsMarkers([]);
    setAllMergeMarkers([]);
    setLimitExceededBySide({ own: null, opponent: null });
    setTimeLineRange({
      min: initialTimelineMin,
      max: initialTimelineMax,
    });
    goalsConcededMarkersRef.current = [];
    goalsConcededOpponentsMarkersRef.current = [];
    timelineMaxRef.current = 0;
  }, [initialTimelineMax, initialTimelineMin]);

  const fetchTeamStatsSide = useCallback(
    async ({ needOpponentMarkers, setMarkersFunc }) => {
      updateLoading(1);

      try {
        const statsResponse = await $apiV1.get(`/stats/team/${id}`, {
          params: {
            matches: matchesList.join(","),
            params: checkedIds.join(","),
            oponnent: needOpponentMarkers,
          },
        });

        const allFetchedMarkers = statsResponse.data.stats.flatMap((stat) =>
          stat.parameters.flatMap((param) => param.markers)
        );

        const parameterColorMap = buildParameterColorMap(checkedAction);
        const markerColorMap = buildMarkerColorMap(
          statsResponse?.data?.stats,
          parameterColorMap
        );

        if (includeGoalsConceded) {
          const gcMarkers = checkedIds.includes(2)
            ? statsResponse.data.stats.flatMap((stat) =>
                stat.parameters
                  .filter((param) => param.parameter_id === 2)
                  .flatMap((param) => param.markers)
              )
            : [];

          if (needOpponentMarkers) {
            goalsConcededOpponentsMarkersRef.current = gcMarkers;
          } else {
            goalsConcededMarkersRef.current = gcMarkers;
          }
        }

        if (needOpponentMarkers) {
          setOpponentMarkerColorMap(markerColorMap);
        } else {
          setOwnMarkerColorMap(markerColorMap);
        }

        setMarkersFunc?.(allFetchedMarkers);
      } catch (error) {
        onErrorRef.current?.(error);
        console.error("Error fetching team stats:", error);
      } finally {
        updateLoading(-1);
      }
    },
    [
      checkedAction,
      checkedIds,
      id,
      includeGoalsConceded,
      matchesList,
      updateLoading,
    ]
  );

  const searchEventsForSide = useCallback(
    async ({ markersToSearch, side }) => {
      updateLoading(1);

      try {
        const goalsConceded =
          side === "opponent"
            ? goalsConcededOpponentsMarkersRef.current
            : goalsConcededMarkersRef.current;

        const basePayload =
          buildSearchPayload?.({
            markers: markersToSearch,
            side,
            goalsConceded,
          }) || {};

        const payload = {
          ...basePayload,
          markers: basePayload.markers || markersToSearch,
          to_merge: false,
        };

        if (
          includeGoalsConceded &&
          goalsConceded.length > 0 &&
          !payload.goals_conceded
        ) {
          payload.goals_conceded = goalsConceded;
        }

        // Fast guard: ask the backend only for the count first. If the selection resolves to more
        // events than we allow, surface the count and skip the heavy markers_search entirely.
        const resultCount = await fetchEventsResultCount(payload);
        if (resultCount > EVENTS_RESULT_LIMIT) {
          setLimitExceededBySide((prev) => ({ ...prev, [side]: resultCount }));
          if (side === "opponent") {
            setReponseOpponents(null);
            setAllOpponentsMarkers([]);
          } else {
            setResponse(null);
            setAllOwnMarkers([]);
          }
          setAllMergeMarkers([]);
          return;
        }

        setLimitExceededBySide((prev) => ({ ...prev, [side]: null }));

        const result = await $apiV1.post("/stats/markers_search", payload);

        if (side === "opponent") {
          setReponseOpponents(result.data);
          setAllOpponentsMarkers(result.data.allMarkers || []);
        } else {
          setResponse(result.data);
          setAllOwnMarkers(result.data.allMarkers || []);
        }

        setAllMergeMarkers(result.data.allMarkers || []);

        const currentMax = findMaxSecondMarker(result.data.events || []);
        const maxForTimeline = keepMaxTimeline
          ? Math.max(timelineMaxRef.current, currentMax || 0)
          : currentMax;

        timelineMaxRef.current = maxForTimeline;

        setTimeLineRange({
          min: 0,
          max: Math.ceil(+maxForTimeline / 60 / 5) * 5,
        });
      } catch (error) {
        onErrorRef.current?.(error);
        console.error("Error searching team events:", error);
      } finally {
        updateLoading(-1);
      }
    },
    [
      buildSearchPayload,
      includeGoalsConceded,
      keepMaxTimeline,
      updateLoading,
    ]
  );

  useEffect(() => {
    if (matchesList.length > 0 && checkedIds.length > 0) {
      fetchTeamStatsSide({
        needOpponentMarkers: false,
        setMarkersFunc: setMarkers,
      });
      fetchTeamStatsSide({
        needOpponentMarkers: true,
        setMarkersFunc: setOpponentsMarkers,
      });
      return;
    }

    resetData();
  }, [
    matchesList,
    checkedIds,
    checkedAction,
    fetchTeamStatsSide,
    resetData,
  ]);

  useEffect(() => {
    if (markers.length > 0) {
      searchEventsForSide({ markersToSearch: markers, side: "own" });
    }
  }, [markers, mergeOffset, searchEventsForSide, ...searchDeps]);

  useEffect(() => {
    if (opponentsMarkers.length > 0) {
      searchEventsForSide({
        markersToSearch: opponentsMarkers,
        side: "opponent",
      });
    }
  }, [opponentsMarkers, mergeOffset, searchEventsForSide, ...searchDeps]);

  return {
    markers,
    setMarkers,
    opponentsMarkers,
    setOpponentsMarkers,
    response,
    setResponse,
    responseOpponents,
    setReponseOpponents,
    ownMarkerColorMap,
    setOwnMarkerColorMap,
    opponentMarkerColorMap,
    setOpponentMarkerColorMap,
    allOwnMarkers,
    allOpponentsMarkers,
    allMergeMarkers,
    loadingMarkers,
    limitExceededBySide,
    timeLineRange,
    setTimeLineRange,
    goalsConcededMarkersRef,
    goalsConcededOpponentsMarkersRef,
    resetData,
  };
};

export default useTeamDualSideEventsData;
